import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CircleNotch, Globe, MagnifyingGlass, Sparkle } from '@phosphor-icons/react';

import { getOrgGeneral } from '@/api/orgs';
import {
  analyzeLogo,
  createWebsite,
  generateWebsitePage,
  getWebsiteDesign,
  importBrand,
  publishWebsite,
  setDesignMode,
  setDesignSiteEntries,
  setDesignTheme,
  WEBSITE_TEMPLATES,
  type BrandImport,
  type PageContent,
  type SiteEntry,
  type ThemeOverrides,
  type WebsiteModule,
  type WebsiteTemplate,
} from '@/api/websites';
import { ApiError } from '@/api/client';
import { GETTING_STARTED_QUERY_KEY } from '@/components/GettingStartedCard';
import { useConfirm } from '@/components/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { WizardDialog } from '@/components/setup/WizardDialog';

type Phase = 'intro' | 'generating' | 'done';

const DEFAULT_MODULES: WebsiteModule[] = ['news', 'schedule', 'teams', 'about', 'contact'];

// The AI pages we try to generate (best-effort — skipped if Bedrock is off).
// Each brief is expanded with the club name at call time.
const AI_PAGES = ['about', 'join'] as const;

// WebsiteGenerator: create a website DRAFT from a chosen design, themed with the
// org's colors/logo, with AI-written starter pages. Nothing goes live until the
// explicit Publish step (wide blast radius → confirm + edge-cache purge on the
// server). Publishing flips the publish_website milestone → wizard confetti.
export function WebsiteGenerator({
  slug,
  open,
  onOpenChange,
}: {
  slug: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t, i18n } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const [phase, setPhase] = useState<Phase>('intro');
  const [template, setTemplate] = useState<WebsiteTemplate>('classic');
  const [status, setStatus] = useState<string>('');
  const [websiteId, setWebsiteId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // URL-import branch: "start fresh" vs "I already have a website".
  const [mode, setMode] = useState<'new' | 'existing'>('new');
  const [url, setUrl] = useState('');
  const [imported, setImported] = useState<BrandImport | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const orgQuery = useQuery({
    queryKey: ['org-general', slug, 'wizard-website'],
    queryFn: () => getOrgGeneral(token, slug),
    enabled: !!token && open,
  });

  const importMutation = useMutation({
    mutationFn: () => importBrand(token, slug, url),
    onSuccess: (b) => setImported(b),
    onError: () => setImportError(t('setupWizard.website.importError')),
  });

  // Derive the on-brand theme: prefer colors imported from the existing site,
  // then explicit org colors, then the logo palette, else nothing (falls back
  // to design-system defaults server-side).
  async function resolveTheme(): Promise<ThemeOverrides> {
    if (imported && (imported.primary_color || imported.accent_color)) {
      return {
        ...(imported.primary_color ? { primaryColor: imported.primary_color } : {}),
        ...(imported.accent_color ? { accentColor: imported.accent_color } : {}),
      };
    }
    const org = orgQuery.data;
    if (!org) return {};
    if (org.primary_color || org.accent_color) {
      return {
        ...(org.primary_color ? { primaryColor: org.primary_color } : {}),
        ...(org.accent_color ? { accentColor: org.accent_color } : {}),
      };
    }
    if (org.logo_object_key) {
      try {
        const pal = await analyzeLogo(token, slug, org.logo_object_key);
        return {
          ...(pal.primary ? { primaryColor: pal.primary } : {}),
          ...(pal.accent ? { accentColor: pal.accent } : {}),
        };
      } catch {
        return {};
      }
    }
    return {};
  }

  // Best-effort AI page generation. Returns [] when Bedrock is unavailable so
  // the draft still gets created with default seed copy.
  async function generatePages(id: string): Promise<PageContent[]> {
    const org = orgQuery.data;
    const orgName = org?.name ?? '';
    const pages: PageContent[] = [];
    // If we imported hints from the club's existing site, feed the description
    // to the model as extra context so the copy sounds like this club.
    const context = imported?.description ? ` Context from their current site: ${imported.description}` : '';
    for (const key of AI_PAGES) {
      try {
        const brief = t(`setupWizard.website.briefs.${key}`, { org: orgName }) + context;
        const page = await generateWebsitePage(token, slug, id, brief, i18n.language);
        pages.push(page);
      } catch (e) {
        // 503 = AI off; any other failure we also just skip the page.
        if (e instanceof ApiError && e.status === 503) break;
      }
    }
    return pages;
  }

  const generateMutation = useMutation({
    mutationFn: async () => {
      const org = orgQuery.data;
      const name = org?.name || t('setupWizard.website.defaultName');

      setStatus(t('setupWizard.website.statusCreating'));
      const site = await createWebsite(token, slug, {
        name,
        template,
        hero_seed: 1,
        enabled_modules: DEFAULT_MODULES,
      });

      setStatus(t('setupWizard.website.statusTheming'));
      const theme = await resolveTheme();
      if (Object.keys(theme).length > 0) {
        await setDesignTheme(token, slug, site.id, theme);
      }

      setStatus(t('setupWizard.website.statusWriting'));
      const generated = await generatePages(site.id);

      if (generated.length > 0) {
        // Append the AI pages to the auto-seeded nav so the system routes
        // (home/schedule/teams/…) are preserved. De-dupe slugs client-side.
        const design = await getWebsiteDesign(token, slug, site.id, i18n.language);
        const used = new Set<string>();
        for (const e of design.site_entries) if (e.page?.slug) used.add(e.page.slug);
        const extra: SiteEntry[] = generated.map((page) => {
          let s = page.slug || 'page';
          while (used.has(s)) s = `${s}-2`;
          used.add(s);
          return {
            id: crypto.randomUUID(),
            label: page.title,
            kind: 'page',
            page: { ...page, slug: s },
            show_in_nav: true,
          };
        });
        setStatus(t('setupWizard.website.statusFinishing'));
        await setDesignSiteEntries(token, slug, site.id, [...design.site_entries, ...extra]);
        await setDesignMode(token, slug, site.id, true);
      }

      return site.id;
    },
    onSuccess: (id) => {
      setWebsiteId(id);
      setPhase('done');
      queryClient.invalidateQueries({ queryKey: ['websites', slug] });
    },
    onError: () => {
      setError(t('setupWizard.website.error'));
      setPhase('intro');
    },
  });

  const publishMutation = useMutation({
    mutationFn: async () => {
      if (!websiteId) return false;
      const ok = await confirm({
        title: t('setupWizard.website.publishConfirmTitle'),
        body: t('setupWizard.website.publishConfirmBody'),
        confirmLabel: t('setupWizard.website.publishConfirmCta'),
      });
      if (!ok) return false;
      await publishWebsite(token, slug, websiteId);
      return true;
    },
    onSuccess: (published) => {
      if (!published) return;
      queryClient.invalidateQueries({ queryKey: GETTING_STARTED_QUERY_KEY(slug) });
      queryClient.invalidateQueries({ queryKey: ['websites', slug] });
      onOpenChange(false);
    },
    onError: () => setError(t('setupWizard.website.publishError')),
  });

  const handleOpenChange = (next: boolean) => {
    onOpenChange(next);
    if (!next) {
      setPhase('intro');
      setWebsiteId(null);
      setStatus('');
      setError(null);
      setMode('new');
      setUrl('');
      setImported(null);
      setImportError(null);
    }
  };

  return (
    <WizardDialog
      open={open}
      onOpenChange={handleOpenChange}
      icon={Globe}
      title={t('setupWizard.website.title')}
      subtitle={t('setupWizard.website.subtitle')}
    >
      {renderBody()}
    </WizardDialog>
  );

  function renderBody() {
    if (phase === 'generating') {
      return (
        <div className="flex flex-col items-center gap-4 py-8 text-center">
          <CircleNotch size={32} className="animate-spin text-primary motion-reduce:animate-none" />
          <p className="text-sm text-muted-foreground">{status || t('common.loading')}</p>
        </div>
      );
    }
    if (phase === 'done') {
      return (
        <div className="flex flex-col items-start gap-4 py-2">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Sparkle size={18} weight="fill" className="text-primary" />
            {t('setupWizard.website.doneTitle')}
          </div>
          <p className="text-sm text-muted-foreground">{t('setupWizard.website.doneBody')}</p>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <Button
              variant="outline"
              onClick={() => {
                handleOpenChange(false);
                if (websiteId) navigate(`/orgs/${slug}/website`);
              }}
            >
              {t('setupWizard.website.reviewLater')}
            </Button>
            <Button onClick={() => publishMutation.mutate()} disabled={publishMutation.isPending}>
              <Globe size={16} weight="bold" />
              {publishMutation.isPending
                ? t('setupWizard.website.publishing')
                : t('setupWizard.website.publishCta')}
            </Button>
          </div>
        </div>
      );
    }
    // intro — mode toggle + optional URL import + design picker
    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm text-muted-foreground">{t('setupWizard.website.intro')}</p>

        <div className="flex gap-2" role="radiogroup" aria-label={t('setupWizard.website.modeLabel')}>
          {(['new', 'existing'] as const).map((m) => (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={mode === m}
              onClick={() => setMode(m)}
              className={
                'flex-1 rounded-pill border px-3 py-2 text-sm font-semibold transition-colors ' +
                (mode === m ? 'border-primary bg-primary/10 text-foreground' : 'border-border text-muted-foreground hover:bg-muted/50')
              }
            >
              {t(`setupWizard.website.mode.${m}`)}
            </button>
          ))}
        </div>

        {mode === 'existing' && (
          <div className="flex flex-col gap-2 rounded-xl border border-border bg-muted/30 p-4">
            <label htmlFor="wizard-site-url" className="text-sm font-medium text-foreground">
              {t('setupWizard.website.urlLabel')}
            </label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                id="wizard-site-url"
                type="url"
                inputMode="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://myclub.se"
                className="h-10 flex-1 rounded-xl border border-border bg-background px-3 text-sm outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-ring"
              />
              <Button
                variant="secondary"
                onClick={() => {
                  setImportError(null);
                  importMutation.mutate();
                }}
                disabled={!url.trim() || importMutation.isPending}
              >
                <MagnifyingGlass size={16} weight="bold" />
                {importMutation.isPending ? t('setupWizard.website.importing') : t('setupWizard.website.importCta')}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">{t('setupWizard.website.urlHint')}</p>
            {importError && <p className="text-sm text-destructive">{importError}</p>}
            {imported && (
              <div className="flex items-center gap-2 text-sm text-foreground">
                <Sparkle size={16} weight="fill" className="text-primary" />
                {t('setupWizard.website.imported')}
                {(imported.primary_color || imported.accent_color) && (
                  <span className="flex items-center gap-1">
                    {imported.primary_color && (
                      <span className="h-4 w-4 rounded-full border border-border" style={{ backgroundColor: imported.primary_color }} />
                    )}
                    {imported.accent_color && (
                      <span className="h-4 w-4 rounded-full border border-border" style={{ backgroundColor: imported.accent_color }} />
                    )}
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        <p className="text-sm font-medium text-foreground">{t('setupWizard.website.pickDesign')}</p>
        <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {WEBSITE_TEMPLATES.map((tpl) => {
            const active = template === tpl;
            return (
              <li key={tpl}>
                <button
                  type="button"
                  onClick={() => setTemplate(tpl)}
                  aria-pressed={active}
                  className={
                    'flex w-full flex-col items-start rounded-xl border p-4 text-left transition-colors ' +
                    (active ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50')
                  }
                >
                  <span className="text-sm font-semibold text-foreground">
                    {t(`setupWizard.website.templates.${tpl}.name`)}
                  </span>
                  <span className="mt-0.5 text-xs text-muted-foreground">
                    {t(`setupWizard.website.templates.${tpl}.description`)}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => {
              setError(null);
              setPhase('generating');
              generateMutation.mutate();
            }}
            disabled={orgQuery.isLoading || generateMutation.isPending}
          >
            <Sparkle size={16} weight="fill" />
            {t('setupWizard.website.createCta')}
          </Button>
        </div>
      </div>
    );
  }
}
